import { useEffect, useRef, useState } from "react";
import api from "@/api/client";
import { Bell, PackageCheck } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Link } from "react-router-dom";

export default function NotificationsBell() {
  const [data, setData] = useState({ items: [], unread: 0 });
  const [open, setOpen] = useState(false);
  const timer = useRef();

  const load = async () => {
    try { const { data } = await api.get("/notifications", { params: { limit: 8 } }); setData(data); } catch {}
  };

  useEffect(() => {
    load();
    timer.current = setInterval(load, 20000);
    return () => clearInterval(timer.current);
  }, []);

  const markRead = async (id) => {
    await api.post(`/notifications/${id}/read`);
    load();
  };

  const markAll = async () => {
    await api.post("/notifications/mark-all-read");
    load();
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button data-testid="notif-bell" className="relative border border-white/25 bg-white/10 text-white p-2 rounded-md hover:bg-white hover:text-primary transition-colors">
          <Bell size={16} />
          {data.unread > 0 && (
            <span data-testid="notif-badge" className="absolute -top-1 -right-1 bg-white text-primary text-[10px] mono px-1 min-w-[16px] h-4 flex items-center justify-center font-bold rounded-full ring-2 ring-primary">
              {data.unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="rounded-md p-0 w-[380px]">
        <div className="flex items-center justify-between p-3 border-b border-border">
          <div className="overline">Notifications</div>
          <button onClick={markAll} data-testid="notif-mark-all" className="text-[11px] mono uppercase tracking-widest hover:text-primary">
            Mark all read
          </button>
        </div>
        <div className="max-h-96 overflow-y-auto">
          {data.items.length === 0 && <div className="p-6 text-center text-sm text-muted-foreground">All caught up.</div>}
          {data.items.map(n => (
            <button
              key={n.id}
              onClick={() => markRead(n.id)}
              data-testid={`notif-item-${n.id}`}
              className={`w-full text-left px-3 py-3 border-b border-border/50 hover:bg-primary/5 ${!n.read ? "bg-primary/5" : ""}`}
            >
              <div className="flex items-start gap-2">
                <PackageCheck size={14} className={n.read ? "text-muted-foreground mt-0.5" : "text-primary mt-0.5"} />
                <div className="flex-1">
                  <div className="text-sm font-semibold">{n.recipient_name}</div>
                  <div className="text-xs text-muted-foreground">{n.message}</div>
                  <div className="text-[10px] mono text-muted-foreground mt-1">
                    {n.tracking_number} · {n.courier} · {new Date(n.created_at).toLocaleString()}
                  </div>
                </div>
                {!n.read && <span className="w-2 h-2 rounded-full bg-primary mt-1.5" />}
              </div>
            </button>
          ))}
        </div>
        <div className="p-3 border-t border-border">
          <Link to="/app/notifications" onClick={() => setOpen(false)} data-testid="notif-view-all" className="text-xs mono uppercase tracking-widest hover:text-primary">
            View all →
          </Link>
        </div>
      </PopoverContent>
    </Popover>
  );
}
